/* eslint-disable @typescript-eslint/naming-convention */
const listeners_description_map = new WeakMap();
const listeners_instance_storage_map = new WeakMap();
/**
 * Decorator used to define element as Custom Element with a specifed tag.
 * Handles attributes changes and attaches/reataches/detaches DOM listeners for the element.
 */
export function CustomElement(tag) {
    return function (constructor) {
        // Define observedAttributes
        Object.defineProperty(constructor, 'observedAttributes', {
            get: function () {
                return constructor['observedAttributesArray'] || [];
            }
        });
        // Wrap render
        const original_render = constructor.prototype.render;
        constructor.prototype.render = function () {
            // Call the original method
            if (!original_render) {
                throw new Error('render method must be defined');
            }
            original_render.call(this);
            // Create listeners storage
            let listeners_storage = listeners_instance_storage_map.get(this);
            if (!listeners_storage) {
                listeners_storage = new Map();
                listeners_instance_storage_map.set(this, listeners_storage);
            }
            // Unregister event listeners
            listeners_storage.forEach(({ element, type }, method) => element.removeEventListener(type, method));
            listeners_storage.clear();
            // Register event listeners
            const event_listeners = listeners_description_map.get(constructor);
            if (event_listeners && this.shadowRoot) {
                event_listeners.forEach(description => {
                    const elements = this.shadowRoot.querySelectorAll(description.selector);
                    elements.forEach(element => {
                        const bound_method = this[description.method_name].bind(this);
                        element.addEventListener(description.type, bound_method);
                        listeners_storage.delete(bound_method);
                        listeners_storage.set(bound_method, { element: element, type: description.type });
                    });
                });
            }
        };
        // Wrap attributeChangedCallback
        const originalAttributeChangedCallback = constructor.prototype.attributeChangedCallback;
        constructor.prototype.attributeChangedCallback = function (name, oldValue, newValue) {
            // Call the original method if it was defined
            if (originalAttributeChangedCallback) {
                originalAttributeChangedCallback.call(this, name, oldValue, newValue);
            }
            if (oldValue !== newValue) {
                this[name] = newValue;
                this.render();
            }
        };
        // Define the custom element with the provided tag
        if (!window.customElements.get(tag)) {
            window.customElements.define(tag, constructor);
        }
    };
}
/**
 * Defines property as element attribute.
 * Changing the attribute via `element.setAttribute` or via DOM `<element attributename="value">` will cause element to rerender.
 */
export function Attribute(target, propertyKey) {
    if (!target.constructor.observedAttributesArray) {
        target.constructor.observedAttributesArray = [];
    }
    target.constructor.observedAttributesArray.push(propertyKey);
}
/**
 * Defines a method as a callback for a specified event on a DOM element.
 */
export function OnEvent(type, selector) {
    return function (target, method_name, descriptor) {
        let event_listeners = listeners_description_map.get(target.constructor);
        if (!event_listeners) {
            event_listeners = [];
            listeners_description_map.set(target.constructor, event_listeners);
        }
        event_listeners.push({ type: type, selector: selector, method_name: method_name });
    };
}
//# sourceMappingURL=CustomElement.js.map